import { useState } from 'react';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/shared/auth/useAuth';

import { registrationErrorMessage, useRegisterCompany } from '@/features/registration';

const STEPS = ['Company', 'Owner', 'Review'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Public company sign-up wizard → provisions a tenant + owner via the BFF. */
export function SignUpPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');

  const mutation = useRegisterCompany();

  const companyValid = companyName.trim().length >= 2;
  const ownerValid = ownerName.trim().length >= 2 && EMAIL_RE.test(ownerEmail.trim());

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = () => {
    mutation.mutate({
      companyName: companyName.trim(),
      ownerName: ownerName.trim(),
      ownerEmail: ownerEmail.trim(),
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 520 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          {mutation.isSuccess ? (
            <Stack spacing={2} alignItems="center" textAlign="center">
              <MarkEmailReadRoundedIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="h5">Check your inbox</Typography>
              <Typography variant="body2" color="text.secondary">
                We&apos;ve sent a verification link to <strong>{ownerEmail}</strong>. Verify your email
                and set a password to finish creating <strong>{companyName}</strong>.
              </Typography>
              <Button variant="contained" onClick={login}>
                Go to sign in
              </Button>
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h5" component="h1">
                  Create a company account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Set up your store&apos;s back office in a couple of steps.
                </Typography>
              </Box>

              <Stepper activeStep={step} alternativeLabel>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {mutation.isError ? (
                <Alert severity="error">{registrationErrorMessage(mutation.error)}</Alert>
              ) : null}

              {step === 0 ? (
                <TextField
                  label="Company name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  fullWidth
                  required
                  autoFocus
                  helperText="The legal or trading name of your company."
                />
              ) : null}

              {step === 1 ? (
                <Stack spacing={2}>
                  <TextField
                    label="Your name"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Work email"
                    type="email"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    fullWidth
                    required
                    error={ownerEmail.length > 0 && !EMAIL_RE.test(ownerEmail.trim())}
                    helperText="You'll verify this email and set a password."
                  />
                </Stack>
              ) : null}

              {step === 2 ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Review</Typography>
                  <ReviewRow label="Company" value={companyName} />
                  <ReviewRow label="Owner" value={ownerName} />
                  <ReviewRow label="Email" value={ownerEmail} />
                </Stack>
              ) : null}

              <Stack direction="row" justifyContent="space-between">
                <Button
                  startIcon={<ArrowBackRoundedIcon />}
                  color="inherit"
                  onClick={step === 0 ? () => navigate('/welcome') : back}
                  disabled={mutation.isPending}
                >
                  {step === 0 ? 'Sign in' : 'Back'}
                </Button>

                {step < STEPS.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={next}
                    disabled={step === 0 ? !companyValid : !ownerValid}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={submit}
                    disabled={mutation.isPending || !companyValid || !ownerValid}
                    startIcon={
                      mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
                    }
                  >
                    Create company
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value || '—'}
      </Typography>
    </Stack>
  );
}
